/** How long a damage chip holds before it drains. A new hit restarts the hold. */
export const DAMAGE_CHIP_HOLD_MS = 700;
/** How long a damage chip takes to drain away. */
export const DAMAGE_CHIP_DRAIN_MS = 450;

/**
 * A bar's damage chip (#201): the span lost to recent hits, shown in a
 * lighter tint from the value before them down to the current value, with
 * the total taken.
 */
export interface DamageChipState {
    /** The value before the hits; null with no chip. */
    readonly fromValue: number | null;
    /** Everything those hits took. */
    readonly taken: number;
    /** The hold is over and the chip is draining to the current value. */
    readonly draining: boolean;
    /** Changes with each new chip, so its number pops again. */
    readonly id: number;
}

export const IDLE_DAMAGE_CHIP: DamageChipState = { fromValue: null, taken: 0, draining: false, id: 0 };

/**
 * The chip after the bar's value changes from `previous` to `next`. A hit
 * starts a chip, or grows the one holding; a hit on a draining chip starts a
 * fresh one. Healing back up to the chip's start clears it.
 */
export function applyEssenceChange(state: DamageChipState, previous: number, next: number): DamageChipState {
    if (next < previous) {
        const isFresh = state.fromValue === null || state.draining;
        return {
            fromValue: isFresh ? previous : state.fromValue,
            taken: (isFresh ? 0 : state.taken) + (previous - next),
            draining: false,
            id: isFresh ? state.id + 1 : state.id,
        };
    }
    if (next > previous && state.fromValue !== null && next >= state.fromValue) {
        return { ...IDLE_DAMAGE_CHIP, id: state.id };
    }
    return state;
}
